import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, AlertController, IonSearchbar } from '@ionic/angular/lazy';
import { Capacitor } from '@capacitor/core';
import { StorageService } from 'src/app/services/storage.service';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

@Component({
    selector: 'app-address-finder',
    templateUrl: './address-finder.page.html',
    styleUrls: ['./address-finder.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddressFinderPage implements OnInit {

    @ViewChild('search_bar_input', { static: false }) search_bar_input: IonSearchbar;

    @Input() country_code = null;
    @Input() subdivision_required = false;
    search_query = '';
    suggestions = null;
    loading = false;
    modal_css_class = '';
    google_maps_connected = false;
    places_library = null;
    session_token = null;

    constructor(
        private modalController: ModalController,
        private alertController: AlertController,
        private storageService: StorageService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
        }

        if(this.subdivision_required) {
            this.subdivision_required = true;
        }

        this.storageService.get('google_maps_public_api_key').then((google_maps_public_api_key) => {
            if(google_maps_public_api_key) {
                setOptions({ key: google_maps_public_api_key });
                importLibrary('places').then((places_library) => {
                    this.places_library = places_library;
                    this.session_token = new this.places_library.AutocompleteSessionToken();
                    this.google_maps_connected = true;
                })
                .catch(() => {
                    this.presentAlert("Error", "Unable to load address search. Please try again.");
                });
            }
        })
        .catch(() => { });

    }
    
    ionViewDidEnter() {
        setTimeout(() => {
            if(this.search_bar_input) {
                this.search_bar_input.setFocus();
            }
        }, 200);
    }
    
    close() {
        this.modalController.dismiss();
    }    


    searchAddresses() {

        if(!this.search_query || this.search_query.trim().length < 3) {
            this.suggestions = null;
            return;
        }

        if(!this.session_token) {
            return;
        }

        this.loading = true;
        
        this.places_library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: this.search_query,
            includedRegionCodes: [this.country_code.toLowerCase()],
            includedPrimaryTypes: ['street_address', 'subpremise', 'premise'],
            sessionToken: this.session_token
        }).then((response) => {
            this.loading = false;
            if(response && response.suggestions) {
                this.suggestions = response.suggestions;
            }
            else {
                this.suggestions = [];
            }
        })
        .catch(() => {
            this.loading = false;
            this.suggestions = [];
        });

    }

    retrieveAddress(suggestion) {

        this.loading = true;

        let place = suggestion.placePrediction.toPlace();

        place.fetchFields({ fields: ['addressComponents', 'displayName'] }).then(() => {
            this.loading = false;
            let address = this.parseAddressComponents(place);
            this.modalController.dismiss(address);
        })
        .catch(() => {
            this.loading = false;
            this.presentAlert("Error", "Unable to retrieve address details. Please try again.");
        });

    }

    parseAddressComponents(place) {

        let street_number = '';
        let route = '';
        let sublocality = '';
        let locality = '';
        let postal_town = '';
        let admin_1 = '';
        let postal_code = '';
        let premise = '';
        let subpremise = '';
        let place_name = place.displayName || '';

        if(place.addressComponents) {

            for(let i = 0; i < place.addressComponents.length; i++) {

                let component = place.addressComponents[i];
                let types = component.types;
                let long_name = component.longText || '';
                let short_name = component.shortText || '';

                if(types.indexOf('street_number') > -1) {
                    street_number = short_name;
                }
                if(types.indexOf('route') > -1) {
                    route = long_name;
                }
                if(types.indexOf('sublocality_level_1') > -1 || types.indexOf('sublocality') > -1) {
                    sublocality = long_name;
                }
                if(types.indexOf('locality') > -1) {
                    locality = long_name;
                }
                if(types.indexOf('postal_town') > -1) {
                    postal_town = long_name;
                }
                if(types.indexOf('administrative_area_level_1') > -1) {
                    admin_1 = long_name;
                }
                if(types.indexOf('postal_code') > -1) {
                    postal_code = long_name;
                }
                if(types.indexOf('premise') > -1) {
                    premise = long_name;
                }
                if(types.indexOf('subpremise') > -1) {
                    subpremise = long_name;
                }
            }
        }

        let premises = '';
        if(subpremise && premise) {
            premises = subpremise + ', ' + premise;
        }
        else if(subpremise) {
            premises = subpremise;
        }
        else if(premise) {
            premises = premise;
        }

        let line_1 = street_number ? street_number + ' ' + route : route;
        if(!line_1 && place_name) {
            line_1 = place_name;
        }

        let town = postal_town || locality || sublocality;

        let line_2 = '';
        if(sublocality && town && sublocality !== town) {
            line_2 = sublocality;
        }
        if(postal_town && locality && postal_town !== locality) {
            line_2 = locality;
        }

        return {
            premises: premises,
            line_1: line_1,
            line_2: line_2,
            locality: town,
            subdivision: this.subdivision_required ? admin_1 : '',
            postal_code: postal_code
        };
    }    

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }    

}
